import React, { useState } from "react";
import { X, Code, Copy, Check } from "lucide-react";

const DetailsModal = ({ row, onClose, clientName }) => {
    const [copied, setCopied] = useState(false);

    if (!row) return null;

    const handleCopy = () => {
        const jsonString = JSON.stringify(row, null, 2);
        navigator.clipboard.writeText(jsonString).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const cleanHTML = (value) => {
        if (!value) return "";
        return value.replace(/<[^>]*>/g, "").trim();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200">

                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-slate-100">
                    <div>
                        <h3 className="text-lg font-bold text-slate-900">File Details</h3>
                        <p className="text-sm text-slate-500 font-mono mt-1">{row.fileName}</p>
                    </div>
                    <button onClick={onClose} className="p-2 text-slate-400 hover:bg-slate-100 rounded-full transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {/* Scrollable Content */}
                <div className="p-6 overflow-y-auto flex-1">

                    <div className="grid grid-cols-2 gap-y-6 gap-x-4 mb-8">
                        {/* Status */}
                        <div className="col-span-2 sm:col-span-1">
                            <p className="text-xs font-semibold text-slate-400 uppercase mb-1">Client</p>
                            <p className="text-sm font-medium text-slate-700">{clientName[row.clientId] || row.clientId}</p>
                        </div>

                        {/* Stage */}
                        <div className="col-span-2 sm:col-span-1">
                            <p className="text-xs font-semibold text-slate-400 uppercase mb-1">Source</p>
                            <p className="text-sm font-medium text-slate-700">{row.source}</p>
                        </div>

                        <div className="col-span-2"><div className="h-px bg-slate-100 w-full" /></div>

                        {/* Other Fields */}
                        <div>
                            <p className="text-xs font-semibold text-slate-400 uppercase mb-1">File ID</p>
                            <p className="text-sm font-mono text-slate-600 bg-slate-50 px-2 py-1 rounded w-fit">{row.fileId}</p>
                        </div>

                        <div>
                            <p className="text-xs font-semibold text-slate-400 uppercase mb-1">File Name</p>
                            <p className="text-sm font-mono text-slate-600 truncate">{cleanHTML(row.filename)}</p>
                        </div>

                        <div className="col-span-2"><div className="h-px bg-slate-100 w-full" /></div>

                        <div>
                            <p className="text-xs font-semibold text-slate-400 uppercase mb-1">Start Time</p>
                            <p className="text-sm text-slate-600">{cleanHTML(row.start_time)}</p>
                        </div>

                        <div>
                            <p className="text-xs font-semibold text-slate-400 uppercase mb-1">End Time</p>
                            <p className="text-sm text-slate-600">{cleanHTML(row.end_time)}</p>
                        </div>
                    </div>

                    {/* JSON Section */}
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                                <Code size={16} className="text-blue-500" />
                                Full JSON Data
                            </h4>

                            <button
                                onClick={handleCopy}
                                className={`text-xs flex items-center gap-1 px-2 py-1 rounded border transition-all ${
                                    copied
                                        ? "bg-emerald-50 text-emerald-600 border-emerald-200"
                                        : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
                                }`}
                            >
                                {copied ? <Check size={12} /> : <Copy size={12} />}
                                {copied ? "Copied" : "Copy JSON"}
                            </button>
                        </div>

                        <div className="bg-slate-900 rounded-lg p-4 overflow-x-auto border border-slate-800 shadow-inner">
                            <pre className="text-xs text-emerald-400 font-mono whitespace-pre-wrap break-all">
                                {JSON.stringify(row, null, 2)}
                            </pre>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-2">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-white hover:shadow-sm hover:text-slate-900 border hover:border-slate-200 rounded-lg"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DetailsModal;
